// frontend/src/components/LessonContent.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Clock, Download, CheckCircle, ArrowRight } from 'lucide-react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { apiUrl } from '../config/api';

interface User {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  status: string;
}

interface LessonContentProps {
  user: User;
  onLogout: () => void;
}

interface LessonData {
  moduleId: number;
  lessonId: number;
  title: string;
  description: string;
  content: {
    type: 'video' | 'text' | 'interactive';
    data: any;
  };
  duration: string;
  resources: {
    title: string;
    type: 'pdf' | 'template' | 'checklist' | 'link';
    url: string;
    downloadable: boolean;
  }[];
  assignments: {
    title: string;
    description: string;
    type: 'quiz' | 'form' | 'upload';
    required: boolean;
  }[];
}

export function LessonContent({ user, onLogout }: LessonContentProps) {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<LessonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(apiUrl(`/api/lessons/${moduleId}/${lessonId}`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch lesson');
        }

        const data = await response.json();
        setLesson(data);
      } catch (error) {
        console.error('Error fetching lesson:', error);
      } finally {
        setLoading(false);
      }
    };

    if (moduleId && lessonId) {
      fetchLesson();
    }
  }, [moduleId, lessonId]);

  const handleComplete = async () => {
    if (!lesson || completing) return;

    setCompleting(true);
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 60000); // Convert to minutes
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(apiUrl(`/api/lessons/${moduleId}/${lessonId}/complete`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ timeSpent })
      });

      if (response.ok) {
        navigate(`/module/${moduleId}`);
      }
    } catch (error) {
      console.error('Error completing lesson:', error);
    } finally {
      setCompleting(false);
    }
  };

  const handleResourceDownload = (filename: string) => {
    try {
      // Use direct public URL for downloads
      const link = document.createElement('a');
      link.href = `/downloads/${filename}`;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'var(--white)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Navbar user={user} onLogout={onLogout} />
        <main style={{ flex: 1 }}>
          <div className="container">
            <div className="loading-container">Loading lesson...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'var(--white)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Navbar user={user} onLogout={onLogout} />
        <main style={{ flex: 1 }}>
          <div className="container">
            <div className="module-card">
              <h2>Lesson Not Found</h2>
              <Link to={`/module/${moduleId}`} className="try-now-button">
                Back to Module
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--white)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Navbar user={user} onLogout={onLogout} />
      <main style={{ flex: 1 }}>
        <div className="container">
          <Link to={`/module/${moduleId}`} className="back-link">
            <ChevronLeft />
            Back to Module
          </Link>

          <div className="lesson-content">
            <div className="lesson-header">
              <div className="lesson-title-section">
                <h1>{lesson.title}</h1>
                <div className="lesson-meta">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Clock style={{ width: '18px', height: '18px' }} />
                    {lesson.duration}
                  </span>
                </div>
              </div>
            </div>

            <div className="lesson-body">
              {/* Lesson Content */}
              <div className="lesson-content-section">
                {lesson.content.type === 'text' && (
                  <div 
                    dangerouslySetInnerHTML={{ __html: lesson.content.data.html }}
                    style={{ lineHeight: 1.6, color: 'var(--gray)' }}
                  />
                )}
                
                {lesson.content.type === 'video' && (
                  <div className="video-container" style={{ margin: '2rem 0' }}>
                    <iframe
                      src={lesson.content.data.embedUrl}
                      width="100%"
                      height="400"
                      frameBorder="0"
                      allowFullScreen
                      style={{ borderRadius: '8px' }}
                      title="Course Video Content"
                    />
                  </div>
                )}

                {lesson.content.type === 'interactive' && (
                  <div className="interactive-content">
                    {/* Custom interactive content based on lesson.content.data */}
                    <div style={{ padding: '2rem', background: '#f8f9fa', borderRadius: '8px' }}>
                      <h3>Interactive Exercise</h3>
                      <p>{lesson.content.data.description}</p>
                      {/* Add interactive elements here */}
                    </div>
                  </div>
                )}
              </div>

              {/* Resources Section */}
              {lesson.resources.length > 0 && (
                <div className="resources-section" style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                  <h2>Course Materials & Downloads</h2>
                  <div className="resources-grid" style={{ display: 'grid', gap: '1.5rem' }}>
                    {lesson.resources.map((resource, index) => (
                      <div key={index} className="resource-card" style={{ 
                        background: '#f8f9fa', 
                        borderRadius: '8px', 
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }}>
                        <Download style={{ color: 'var(--orange)', width: '1.25rem', height: '1.25rem' }} />
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--dark)', marginBottom: '0.5rem' }}>
                            {resource.title}
                          </h3>
                          <p style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>
                            {resource.type.toUpperCase()} • {resource.downloadable ? 'Downloadable' : 'Link'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleResourceDownload(resource.url)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'var(--dark)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer'
                          }}
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assignments Section */}
              {lesson.assignments.length > 0 && (
                <div className="assignments-section" style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                  <h2>Practice & Assignments</h2>
                  <div className="assignments-list" style={{ display: 'grid', gap: '1.5rem' }}>
                    {lesson.assignments.map((assignment, index) => (
                      <div key={index} className="assignment-card" style={{ 
                        background: '#f8f9fa', 
                        borderRadius: '8px', 
                        padding: '1.5rem' 
                      }}>
                        <div className="assignment-header" style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.75rem', 
                          marginBottom: '1rem' 
                        }}>
                          <CheckCircle style={{ color: 'var(--orange)', width: '1.25rem', height: '1.25rem' }} />
                          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--dark)' }}>
                            {assignment.title}
                            {assignment.required && (
                              <span style={{ color: 'var(--orange)', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                                (Required)
                              </span>
                            )}
                          </h3>
                        </div>
                        <p style={{ color: 'var(--gray)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                          {assignment.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="lesson-navigation" style={{ marginTop: '3rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleComplete}
                  disabled={completing}
                  className="next-lesson-button"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'var(--dark)',
                    color: 'white',
                    padding: '1rem 1.5rem',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: 500,
                    cursor: completing ? 'not-allowed' : 'pointer',
                    opacity: completing ? 0.7 : 1,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {completing ? 'Completing...' : 'Complete Lesson'}
                  <ArrowRight style={{ width: '1.25rem', height: '1.25rem' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}